import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "./Sidebar";
import TopPanel from "./TopPanel";
import Footer from "./Footer";
import { getAllCampaigns } from "../src/Services/campaignService";

// Both servers are kept. The first one is tried first; if it fails, the next one is used.
const API_BASES = (() => {
  const host = window.location.hostname;
  if (host === "crm.whytefarms.com") return ["https://app.whytefarms.com"];      // CRM domain -> app.whytefarms
  if (host === "3.22.224.17") return ["http://13.207.176.232:3000"];             // 3.22 IP -> 13.207 IP
  return ["https://app.whytefarms.com", "http://13.207.176.232:3000"];           // localhost / baaki: pehle domain, phir IP
})();
const REFERRALS_LIST_PATH = "/api/referrals/list";

const fetchWithFallback = async (path, options) => {
  let lastErr;
  for (const base of API_BASES) {
    try {
      const res = await fetch(`${base}${path}`, options);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res;
    } catch (e) {
      lastErr = e;
      console.warn(`[fetch] ${base}${path} failed:`, e.message);
    }
  }
  throw lastErr;
};

function ReferEarnDetails() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const [activeFilter, setActiveFilter] = useState("All");
  const [searchTerm, setSearchTerm] = useState("");

  const [referral, setReferral] = useState(null);
  const [referralsList, setReferralsList] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // 1. Fetch Campaign Details
        const list = await getAllCampaigns("referral");
        setReferral(list[0] || null);

        // 2. Fetch Referrals List API (domain first, IP as fallback)
        const response = await fetchWithFallback(REFERRALS_LIST_PATH);
        const json = await response.json();

        if (json && json.success) {
          setReferralsList(json.data || []);
        } else {
          setReferralsList([]);
        }
      } catch (e) {
        console.error("Error fetching referrals API:", e);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleEdit = () => {
    if (referral) navigate(`/edit-campaign/${referral.id}`);
  };

  // Dynamic Header Info Subtitle
  const getDynamicSubtitle = () => {
    if (!referral) return "—";

    const code = referral.campaignCode || referral.code || referral.id || "WF-REF-0001";
    const startDate = referral.startDate || referral.start_date || "";
    const endDate = referral.endDate || referral.end_date || "";

    if (startDate && endDate) {
      return `${code} · ${startDate} - ${endDate}`;
    }
    return code;
  };

  // --- Dynamic Calculations based on Data ---
  const totalCount = referralsList.length;

  // Registered Count
  const registeredCount = referralsList.filter(
    (i) => i.registered && i.registered !== "—"
  ).length;

  // Subscribers Count
  const subscribersCount = referralsList.filter(
    (i) => i.subscription && i.subscription !== "—" && i.subscription !== "Not yet"
  ).length;

  // Recharge Pending Count
  const rechargePendingCount = referralsList.filter(
    (i) => i.referralStatus === "Recharge pending"
  ).length;

  // Qualifying Recharges Count
  const qualifyingRechargesCount = referralsList.filter(
    (i) => i.referralStatus === "Qualified" || i.referralStatus === "Reward issued"
  ).length;

  // Rewards Issued Count
  const rewardsIssuedCount = referralsList.filter(
    (i) => i.referralStatus === "Reward issued"
  ).length;

  // Subscription Pending Count
  const subscriptionPendingCount = referralsList.filter(
    (i) => i.referralStatus === "Subscription pending"
  ).length;

  // Cancelled / Ineligible Count
  const cancelledCount = referralsList.filter(
    (i) => i.referralStatus === "Cancelled / Ineligible"
  ).length;

  // Total Reward Cost
  const rewardCostCalculated = referralsList.reduce((sum, item) => {
    if (item.referrerReward && item.referrerReward.includes("₹")) {
      const val = parseInt(item.referrerReward.replace(/[^0-9]/g, ""), 10) || 0;
      return sum + val * 2;
    }
    return sum;
  }, 0);

  // Status Badge Helper
  const renderStatusBadge = (status) => {
    let bgColor = "#f1f3f5";
    let textColor = "#495057";

    if (status === "Reward issued") {
      bgColor = "#3b5e40";
      textColor = "#ffffff";
    } else if (status === "Qualified") {
      bgColor = "#e6f0e9";
      textColor = "#2c5a31";
    } else if (status === "Recharge pending" || status === "Subscription pending") {
      bgColor = "#e6f2ff";
      textColor = "#0056b3";
    } else if (status === "Registered") {
      bgColor = "#e9ecef";
      textColor = "#495057";
    } else if (status === "Cancelled / Ineligible") {
      bgColor = "#f8d7da";
      textColor = "#721c24";
    } else if (status === "Flagged for review") {
      bgColor = "#faebd7";
      textColor = "#a0522d";
    }

    return (
      <span style={{ backgroundColor: bgColor, color: textColor, padding: "5px 12px", borderRadius: "15px", fontSize: "13px", fontWeight: "700" }}>
        {status || "—"}
      </span>
    );
  };

  // KPI Card Component
  const KPICard = ({ title, value, progress = 100, color = "#5a3b75", valueColor = "#000" }) => (
    <div className="card shadow-sm h-100" style={{ borderRadius: "10px", border: "1px solid #e0e0e0" }}>
      <div className="card-body py-3">
        <p className="text-muted mb-1" style={{ fontSize: "16px", fontWeight: "600" }}>{title}</p>
        <h3 style={{ fontWeight: "bold", margin: "0 0 10px 0", fontFamily: "serif", color: valueColor, fontSize: "24px" }}>{value}</h3>
        <div style={{ height: "8px", backgroundColor: "#e9ecef", borderRadius: "5px", overflow: "hidden" }}>
          <div style={{ width: `${progress}%`, backgroundColor: color, height: "100%" }}></div>
        </div>
      </div>
    </div>
  );

  // Filter Chip Component
  const FilterChip = ({ text, active = false, onClick }) => (
    <div 
      onClick={onClick}
      style={{
        backgroundColor: active ? "#3b5e40" : "#ffffff",
        color: active ? "#ffffff" : "#495057",
        border: active ? "1px solid #3b5e40" : "1px solid #ced4da",
        padding: "6px 14px",
        borderRadius: "20px",
        fontSize: "14px",
        fontWeight: "600",
        cursor: "pointer",
        whiteSpace: "nowrap",
        transition: "all 0.2s ease-in-out" 
      }}>
      {text}
    </div>
  );

  // Filter & Search Logic
  const filteredData = referralsList.filter((item) => {
    let statusMatch = false;

    if (activeFilter === "All") {
      statusMatch = true;
    } else if (activeFilter === "Registered") {
      statusMatch = item.registered && item.registered !== "—";
    } else if (activeFilter === "Qualified") {
      statusMatch = item.referralStatus === "Qualified" || item.referralStatus === "Reward issued";
    } else {
      statusMatch = item.referralStatus === activeFilter;
    }

    const query = searchTerm.toLowerCase();
    const referrerName = item.referrer?.name?.toLowerCase() || "";
    const referrerCode = item.referrer?.code?.toLowerCase() || "";
    const customerName = item.referredCustomer?.name?.toLowerCase() || "";

    const searchMatch =
      !query ||
      referrerName.includes(query) ||
      referrerCode.includes(query) ||
      customerName.includes(query);

    return statusMatch && searchMatch;
  });

  const filterOptions = [
    { label: "All", key: "All", count: totalCount },
    { label: "Registered", key: "Registered", count: registeredCount },
    { label: "Subscription pending", key: "Subscription pending", count: subscriptionPendingCount },
    { label: "Recharge pending", key: "Recharge pending", count: rechargePendingCount },
    { label: "Qualified", key: "Qualified", count: qualifyingRechargesCount },
    { label: "Reward issued", key: "Reward issued", count: rewardsIssuedCount },
    { label: "Cancelled / Ineligible", key: "Cancelled / Ineligible", count: cancelledCount }
  ];

  return (
    <>
      {loading && (
        <div className="loader-overlay">
          <div><img style={{ height: "6rem" }} src="images/loader.gif" alt="loader" /></div>
        </div>
      )}
      <div className="container-scroller" style={{ backgroundColor: "#f9f7f2" }}>
        <TopPanel />
        <div className="container-fluid page-body-wrapper">
          <Sidebar />
          <div className="main-panel" style={{ backgroundColor: "#f9f7f2", minHeight: "100vh" }}>
            <div className="content-wrapper" style={{ padding: "2rem" }}>
              
              {/* Dynamic Header Section */}
              <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <span style={{ backgroundColor: "#f3e8ff", color: "#5a3b75", padding: "4px 10px", borderRadius: "15px", fontSize: "16px", fontWeight: "700" }}>Refer & Earn</span>
                    <span style={{ color: "#6c757d", fontSize: "16px", fontFamily: "monospace", letterSpacing: "0.5px" }}>
                      {getDynamicSubtitle()}
                    </span>
                  </div>
                  <h2 style={{ fontWeight: "700", fontSize: "28px", fontFamily: "serif", color: "#2c2c2c", margin: 0 }}>
                    {referral?.title || referral?.name || "Refer & Earn"}
                  </h2>
                </div>
                <div>
                  <button
                    className="btn bg-white border fw-bold"
                    style={{ borderRadius: "8px", fontSize: "15px" }}
                    disabled={!referral}
                    onClick={handleEdit}
                  >
                    Edit programme
                  </button>
                </div>
              </div>

              {/* Dynamic KPI Cards */}
              <div className="row mb-4 gx-3">
                <div className="col">
                  <KPICard title="Total referrals" value={totalCount} progress={100} />
                </div>
                <div className="col">
                  <KPICard title="New registrations" value={registeredCount} progress={totalCount ? (registeredCount / totalCount) * 100 : 0} />
                </div>
                <div className="col">
                  <KPICard title="Subscribers" value={subscribersCount} progress={totalCount ? (subscribersCount / totalCount) * 100 : 0} />
                </div>
                <div className="col">
                  <KPICard title="Qualifying recharges" value={qualifyingRechargesCount} progress={totalCount ? (qualifyingRechargesCount / totalCount) * 100 : 0} />
                </div>
                <div className="col">
                  <KPICard title="Rewards issued" value={rewardsIssuedCount} progress={totalCount ? (rewardsIssuedCount / totalCount) * 100 : 0} />
                </div>
                <div className="col">
                  <KPICard title="Reward cost" value={`₹${rewardCostCalculated.toLocaleString("en-IN")}`} progress={80} color="#a56627" valueColor="#a56627" />
                </div>
              </div>

              {/* Table & Filters Section */}
              <div className="card shadow-sm" style={{ borderRadius: "10px", border: "1px solid #e0e0e0" }}>
                <div className="card-body p-0">
                  
                  {/* Filters & Search Header */}
                  <div className="p-3 border-bottom d-flex flex-wrap align-items-start gap-3">
                    {/* Filter Chips */}
                    <div className="d-flex flex-wrap gap-2" style={{ flex: 1, minWidth: "300px" }}>
                      {filterOptions.map((filter, index) => (
                        <FilterChip 
                          key={index}
                          text={`${filter.label} · ${filter.count}`} 
                          active={activeFilter === filter.key} 
                          onClick={() => setActiveFilter(filter.key)} 
                        />
                      ))}
                    </div>

                    {/* Search Input */}
                    <div className="ms-auto" style={{ flexShrink: 0 }}>
                      <input 
                        type="text" 
                        className="form-control form-control-sm" 
                        placeholder="Referrer, customer or code" 
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        style={{ 
                          borderRadius: "6px", 
                          width: "250px", 
                          border: "1px solid #ced4da", 
                          padding: "8px 16px" 
                        }} 
                      />
                    </div>
                  </div>

                  {/* Table */}
                  <div className="table-responsive">
                    <table className="table table-hover mb-0" style={{ fontSize: "12px" }}>
                      <thead style={{ color: "#8a8a8a", fontSize: "10px", letterSpacing: "0.5px", fontWeight: "bold" }}>
                        <tr>
                          <th className="pt-3 pb-3 ps-4 border-0">REFERRER</th>
                          <th className="pt-3 pb-3 border-0">REFERRED<br/>CUSTOMER</th>
                          <th className="pt-3 pb-3 border-0">REGISTERED</th>
                          <th className="pt-3 pb-3 border-0">SUBSCRIPTION</th>
                          <th className="pt-3 pb-3 border-0 text-end">RECHARGE</th>
                          <th className="pt-3 pb-3 border-0">CHECKS</th>
                          <th className="pt-3 pb-3 border-0">REFERRAL STATUS</th>
                          <th className="pt-3 pb-3 border-0 text-end">REFERRER<br/>REWARD</th>
                          <th className="pt-3 pb-3 border-0 text-end pe-4">REFERRED<br/>REWARD</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredData.length > 0 ? (
                          filteredData.map((row) => (
                            <tr key={row.id} style={{ verticalAlign: "middle" }}>
                              <td className="ps-4 py-3">
                                <div style={{ fontWeight: "bold", color: "#2c2c2c" }}>
                                  {row.referrer?.name || "—"}
                                </div>
                                <div style={{ fontSize: "13px", color: "#8a8a8a", fontFamily: "monospace", marginTop: "5px" }}>
                                  {row.referrer?.code || "—"}
                                </div>
                              </td>
                              <td className="py-3">
                                <div style={{ fontWeight: "bold", color: "#2c2c2c" }}>
                                  {row.referredCustomer?.name || "—"}
                                </div>
                                <div style={{ fontSize: "13px", color: "#8a8a8a", marginTop: "5px", textTransform: "capitalize" }}>
                                  {row.referredCustomer?.channel || "—"}
                                </div>
                              </td>
                              <td className="py-3 text-muted" style={{ fontWeight: "700" }}>
                                {row.registered || "—"}
                              </td>
                              <td className="py-3 text-muted" style={{ fontWeight: "700" }}>
                                {row.subscription || "—"}
                              </td>
                              <td className="py-3 text-end text-muted">
                                {row.recharge && row.recharge.includes("refunded") ? (
                                  <span>{row.recharge.split("·")[0]}<br/><span style={{ fontSize: "11px", fontWeight: "700" }}>· refunded</span></span>
                                ) : row.recharge && row.recharge.includes("of") ? (
                                  <span>{row.recharge.split("of")[0]}<br/><span style={{ fontSize: "11px", fontWeight: "700" }}>of {row.recharge.split("of")[1]}</span></span>
                                ) : (
                                  row.recharge || "—"
                                )}
                              </td>
                              <td className="py-3" style={{ color: row.checks === "All passed" ? "#28a745" : "#dc3545", fontSize: "13px", fontWeight: "700" }}>
                                {row.checks === "All passed" ? <span className="text-muted">All passed</span> : (row.checks || "—")}
                              </td>
                              <td className="py-3 text-xl">
                                {renderStatusBadge(row.referralStatus)}
                              </td>
                              <td className="py-3 text-end text-muted" style={{ fontWeight: "700" }}>
                                {row.referrerReward || "—"}
                              </td>
                              <td className="py-3 text-end text-muted pe-4" style={{ fontWeight: "700" }}>
                                {row.referredReward || "—"}
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="9" className="text-center py-4 text-muted">
                              No referrals found.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* Footer Note */}
                  <div className="p-3 border-top" style={{ backgroundColor: "#fafafa", borderBottomLeftRadius: "10px", borderBottomRightRadius: "10px" }}>
                    <p className="text-muted mb-0" style={{ fontSize: "15px" }}>
                      Registered → Subscription pending → Recharge pending → Qualified → Reward issued. Rewards are credited as promotional wallet credit to both customers in one transaction.
                    </p>
                  </div>

                </div>
              </div>

            </div>
            <Footer />
          </div>
        </div>
      </div>
    </>
  );
}

export default ReferEarnDetails;